const { useEffect, useState } = React;
import { dbSubscribeLiveActive, setCaps } from "./data.jsx";
import { useT } from "./i18n.jsx";
import { applySettings } from "./settings.jsx";
import { AdminScreen } from "./screens/Admin.jsx";
import { ConflictDialog, Toasts } from "./team/collab.jsx";
import { PhotoLibraryProvider } from "./photoLibrary.jsx";
import { CalendarScreen } from "./screens/Calendar.jsx";
import { HomeScreen } from "./screens/Home.jsx";
import { LiveModeScreen } from "./screens/Live.jsx";
import { MasterScreen } from "./screens/Master.jsx";
import { PhotoLibraryScreen } from "./screens/PhotoLibrary.jsx";
import { CuePoolScreen } from "./screens/CuePool.jsx";
import { TemplateScreen } from "./screens/Template.jsx";
import { MobileTopBar, Sidebar } from "./shell.jsx";
import { Loader2, T } from "./theme.jsx";

// ============================================================================
// APP
// ============================================================================

function LoadingScreen({ text }) {
  const { t } = useT();
  return (
    <div style={{ background: T.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: T.subtext, fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
      <Loader2 size={16} style={{ animation: "rb-spin 1s linear infinite", marginRight: 8 }} /> {text || t("connecting")}
    </div>
  );
}

// shared hook: which date(s) are currently live, anywhere the shell needs to know
// (the sidebar's quick-jump chip, the mobile top bar, and the Home dashboard).
// `active` gates the subscription until Cap.db is actually set (App's own
// capability-loading effect resolves after this hook's first mount).
function useLiveActive(active) {
  const [liveDates, setLiveDates] = useState({});
  useEffect(() => {
    if (!active) return;
    const unsub = dbSubscribeLiveActive((snap) => {
      const m = {};
      snap.docs.forEach((d) => { m[d.id] = true; });
      setLiveDates(m);
    }, () => {});
    return unsub;
  }, [active]);
  return liveDates;
}

// `session` (from sign-in, see main.jsx) gives the person, role and
// permissions. Without one — when this bundle runs inside a host that
// provides window.claude itself, such as the test harness — it falls back
// to asking that host's `user` capability, as the original artifact did.
function App({ session = null, authConfig = null, onSignOut = null }) {
  const langCtx = useT();
  const { t } = langCtx;
  const [mode, setMode] = useState("home");
  // a day picked on Home opens straight into that day's editor; forgotten as
  // soon as the user leaves the calendar so the sidebar link shows the month
  const [calendarDay, setCalendarDay] = useState(null);
  useEffect(() => { if (mode !== "calendar") setCalendarDay(null); }, [mode]);
  const openDay = (d) => { setCalendarDay(d); setMode("calendar"); };
  const [liveDate, setLiveDate] = useState(null);
  const [ready, setReady] = useState(false);
  const [dbAvailable, setDbAvailable] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [, setSettingsRev] = useState(0);
  const [viewer, setViewer] = useState({ id: null, name: "", isOwner: false, canWrite: null, roleLabel: "Team", role: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const claude = window.claude;
      const db = claude ? await claude.use("db").catch(() => null) : null;
      const assetsCap = claude ? await claude.use("assets").catch(() => null) : null;
      const userCap = claude ? await claude.use("user").catch(() => null) : null;
      const sampleCap = claude ? await claude.use("sample").catch(() => null) : null;
      let next;
      if (session) {
        const u = session.user;
        next = { id: u.id, name: u.name, email: u.email, role: u.role, hasPassword: u.hasPassword, isOwner: u.role === "admin", canWrite: u.role !== "viewer" };
      } else {
        let id = null, name = "", isOwner = false, canWrite = null;
        if (userCap) {
          try {
            const [me, ownerFlag, writeFlag] = await Promise.all([userCap.me(), userCap.isOwner(), userCap.can("data.write")]);
            id = (me && me.id) || null; name = (me && me.name) || ""; isOwner = !!ownerFlag; canWrite = writeFlag;
          } catch (e) {}
        }
        next = { id, name, isOwner, canWrite, role: isOwner ? "admin" : canWrite !== false ? "producer" : "viewer" };
      }
      if (cancelled) return;
      setCaps(db, assetsCap, userCap, sampleCap);
      // admin-editable settings (venue name, weekday programmes, stage presets)
      if (db) {
        try { const snap = await db.collection("settings").doc("app").get(); applySettings(snap.exists ? snap.data() : null); }
        catch (e) { applySettings(null); }
      }
      if (cancelled) return;
      setDbAvailable(!!db);
      setViewer(next);
      setReady(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roleLabel = viewer.role ? t(`role_${viewer.role}`) : "Team";
  const viewerWithLabel = { ...viewer, roleLabel };
  let perms;
  if (session) {
    const p = session.perms;
    perms = { canMaster: p.master, canOnGround: p.onGround, canBroadcast: p.broadcast, canHistory: p.history, isAdmin: p.admin, canAi: p.ai };
  } else {
    const canWriteFlag = viewer.canWrite !== false; // treat unknown (null) as "assume yes, let a refused write decide"
    perms = { canMaster: canWriteFlag, canOnGround: canWriteFlag, canBroadcast: canWriteFlag, canHistory: false, isAdmin: false, canAi: canWriteFlag };
  }
  const readOnly = !perms.canMaster && !perms.canOnGround && !perms.canBroadcast;
  // a descriptive page title per screen (read first by screen readers, and
  // tells tabs apart)
  useEffect(() => {
    const screen = liveDate ? `${t("goLive")} ${liveDate}` : t({ home: "navHome", calendar: "navCalendar", master: "navMaster", template: "navTemplate", photos: "navPhotoLibrary", pool: "navCuePool", admin: "navAdmin" }[mode] || "navHome");
    document.title = `${screen} · ${t("venueName")} — Rundown`;
  });
  const liveDates = useLiveActive(ready && dbAvailable);
  const liveNowKey = Object.keys(liveDates)[0] || null;

  let content;
  if (!ready) {
    content = <LoadingScreen />;
  } else if (!dbAvailable) {
    content = (
      <div style={{ background: T.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ maxWidth: 380 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginBottom: 8 }}>{t("cantOpenNow")}</div>
          <div style={{ fontSize: 13, color: T.subtext }}>{t("cantOpenNowDesc")}</div>
        </div>
      </div>
    );
  } else if (liveDate) {
    content = <LiveModeScreen date={liveDate} perms={perms} onExit={() => setLiveDate(null)} viewer={viewerWithLabel} />;
  } else {
    const goLive = (d) => setLiveDate(d);
    content = (
      <PhotoLibraryProvider>
        <a className="rb-skip" href="#rb-main">{t("skipToContent")}</a>
        <div className="rb-shell" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", background: T.bg, minHeight: "100vh", color: T.text }}>
          <Sidebar
            mode={mode}
            setMode={setMode}
            viewer={viewerWithLabel}
            perms={perms}
            onSignOut={onSignOut}
            liveNowKey={liveNowKey}
            onGoLive={goLive}
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
          />
          <main id="rb-main" tabIndex={-1} className="rb-main" style={{ flex: "1 1 0", minWidth: 0, outline: "none" }}>
            <MobileTopBar mode={mode} setMode={setMode} viewer={viewerWithLabel} perms={perms} onSignOut={onSignOut} />
            {readOnly && (
              <div role="status" style={{ background: T.infoBg, color: T.infoText, fontSize: 13, padding: "8px 16px", textAlign: "center", borderBottom: `1px solid ${T.border}` }}>{t("readOnlyBanner")}</div>
            )}
            {mode === "home" && (
              <HomeScreen perms={perms} viewer={viewerWithLabel} liveNowKey={liveNowKey} onGoLive={goLive} onNavigate={setMode} onOpenDay={openDay} />
            )}
            {mode === "calendar" && <CalendarScreen key={calendarDay ? calendarDay.getTime() : "month"} initialDate={calendarDay} perms={perms} onGotoMaster={() => setMode("master")} onGotoTemplate={() => setMode("template")} onGoLive={goLive} />}
            {mode === "master" && <MasterScreen perms={perms} />}
            {mode === "template" && <TemplateScreen perms={perms} />}
            {mode === "photos" && <PhotoLibraryScreen perms={perms} />}
            {mode === "pool" && <CuePoolScreen perms={perms} />}
            {mode === "admin" && perms.isAdmin && <AdminScreen viewer={viewerWithLabel} authConfig={authConfig} onSettingsChanged={() => setSettingsRev((r) => r + 1)} />}
          </main>
        </div>
      </PhotoLibraryProvider>
    );
  }

  return (
    <>
      {content}
      <ConflictDialog />
      <Toasts />
    </>
  );
}

export { LoadingScreen, useLiveActive, App };
